import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiUrl } from "@/lib/api";

export interface AuthUser {
  readonly id: string;
  readonly phone: string;
  readonly name: string;
  readonly role: "owner" | "manager" | "employee" | "viewer";
  readonly tenantId: string;
  readonly impersonatedBy?: string;
}

export interface AuthContextValue {
  readonly user: AuthUser | null;
  readonly accessToken: string | null;
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  readonly login: (
    accessToken: string,
    user: AuthUser,
  ) => void;
  readonly logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_INTERVAL_MS = 14 * 60 * 1000; // 14 minutes (1 min before 15-min expiry)

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(apiUrl("/api/auth/refresh"), {
          method: "POST",
          credentials: "include",
        });
        if (response.ok) {
          const data = (await response.json()) as {
            readonly data: {
              readonly accessToken: string;
              readonly user: AuthUser;
            };
          };
          setAccessToken(data.data.accessToken);
          setUser(data.data.user);
          scheduleRefresh();
        } else {
          setAccessToken(null);
          setUser(null);
        }
      } catch {
        setAccessToken(null);
        setUser(null);
      }
    }, REFRESH_INTERVAL_MS);
  }, [clearRefreshTimer]);

  const login = useCallback(
    (newAccessToken: string, newUser: AuthUser) => {
      setAccessToken(newAccessToken);
      setUser(newUser);
      scheduleRefresh();
    },
    [scheduleRefresh],
  );

  const logout = useCallback(async () => {
    clearRefreshTimer();
    setAccessToken(null);
    setUser(null);
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Logout best-effort — clear local state regardless
    }
  }, [clearRefreshTimer]);

  // Attempt silent refresh on mount (dev bypass for local testing)
  useEffect(() => {
    let cancelled = false;

    // DEV BYPASS: auto-login as owner in development
    if (import.meta.env.DEV) {
      const devUser: AuthUser = {
        id: "dev-eliya",
        phone: "0501234567",
        name: "Eliya (Dev)",
        role: "owner",
        tenantId: "platform",
      };
      setAccessToken("dev-token");
      setUser(devUser);
      setIsLoading(false);
      return;
    }

    async function silentRefresh(): Promise<void> {
      try {
        const response = await fetch(apiUrl("/api/auth/refresh"), {
          method: "POST",
          credentials: "include",
        });
        if (response.ok && !cancelled) {
          const data = (await response.json()) as {
            readonly data: {
              readonly accessToken: string;
              readonly user: AuthUser;
            };
          };
          setAccessToken(data.data.accessToken);
          setUser(data.data.user);
          scheduleRefresh();
        }
      } catch {
        // Not authenticated — that's fine
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    void silentRefresh();
    return () => {
      cancelled = true;
    };
  }, [scheduleRefresh]);

  // Cleanup timer on unmount
  useEffect(() => {
    return clearRefreshTimer;
  }, [clearRefreshTimer]);

  const value: AuthContextValue = {
    user,
    accessToken,
    isLoading,
    isAuthenticated: user !== null && accessToken !== null,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
